import React, { Fragment, useState, useRef, useEffect } from 'react';
import { StorylineBlockProps } from '@/types/components/blocks';
import { ImageData as CustomImageData } from '@/types/components/image';
import s from './StorylineBlock.module.css';
import { WbTwocolumns } from '@digital-engineering-experiences/wb-webcomponents';
import RawHtml from '@/components/RawHtml';
import EmbeddedImage from '@/components/Image';

const StorylineBlock = ({ data }: { data: StorylineBlockProps }) => {
    const [activeSlide, setActiveSlide] = useState<number | null>(null);
    const rowRefs = useRef<(HTMLDivElement | null)[]>([]);
    const visibleRows = useRef<Set<number>>(new Set());
    const maxSlideReached = useRef<number>(-1);

    // Track which row is in viewport
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const index = rowRefs.current.indexOf(
                        entry.target as HTMLDivElement
                    );

                    if (index !== -1) {
                        if (entry.isIntersecting) {
                            // Add to visible rows
                            visibleRows.current.add(index);
                            setActiveSlide(index);

                            // Track the maximum slide reached
                            if (index > maxSlideReached.current) {
                                maxSlideReached.current = index;
                            }
                        } else {
                            // Remove from visible rows
                            visibleRows.current.delete(index);

                            // If no rows are visible
                            if (visibleRows.current.size === 0) {
                                // Check if we're above or below the component
                                const firstRow = rowRefs.current[0];
                                if (firstRow) {
                                    const rect =
                                        firstRow.getBoundingClientRect();

                                    // If first row is below viewport, we're above the component
                                    if (rect.top > window.innerHeight) {
                                        setActiveSlide(null);
                                    }
                                    // If first row is above viewport, we're below the component
                                    else if (rect.bottom < 0) {
                                        setActiveSlide(
                                            maxSlideReached.current >= 0
                                                ? maxSlideReached.current
                                                : data.rows.length - 1
                                        );
                                    }
                                    // Otherwise show first image as default
                                    else {
                                        setActiveSlide(null);
                                    }
                                }
                            }
                        }
                    }
                });
            },
            {
                root: null,
                rootMargin: '0px',
                threshold: 0.5,
            }
        );

        rowRefs.current.forEach((row) => {
            if (row) {
                observer.observe(row);
            }
        });

        return () => {
            observer.disconnect();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const renderContent = (content: Array<any>) => {
        const contentArray = Array.isArray(content) ? content : [content];

        return (
            <>
                {contentArray.map((item, idx) => {
                    if (item.type === 'Text') {
                        return (
                            <div
                                key={idx}
                                className={s.StorylineBlock__content__text}>
                                <RawHtml html={item.value as string} />
                            </div>
                        );
                    } else if (item.type === 'Image') {
                        return (
                            <span
                                key={idx}
                                className={
                                    s.StorylineBlock__content__placeholder
                                }></span>
                        );
                    } else {
                        return null;
                    }
                })}
            </>
        );
    };

    return (
        <div
            className={s.StorylineBlock__wrapper + ' contentblock'}
            data-active={activeSlide}>
            <div className={s.StorylineBlock}>
                <div className={s.StorylineBlock__images}>
                    <div className={s.StorylineBlock__images__bg}>
                        <EmbeddedImage imageDetails={data.image.img} />
                    </div>
                    {data.rows.map((row, index) => (
                        <div key={index} data-image-index={index}>
                            <Fragment key={index}>
                                {row.columnRight
                                    .filter((item) => item.type == 'Image')
                                    .map((item, index) => (
                                        <EmbeddedImage
                                            key={index}
                                            data={item.value as CustomImageData}
                                            imageDetails={
                                                (item.value as CustomImageData)
                                                    .image.original ||
                                                (item.value as CustomImageData)
                                                    .image.img
                                            }
                                        />
                                    ))}
                            </Fragment>
                        </div>
                    ))}
                </div>
                <div className={s.StorylineBlock__content}>
                    {data.rows.map((row, index) => (
                        <div
                            key={index}
                            data-row-index={index}
                            className={s.StorylineBlock__content__row}
                            ref={(el) => {
                                rowRefs.current[index] = el;
                            }}>
                            <WbTwocolumns
                                inverse={row.columnOrder == 'invert' && true}
                                variant={row.grid}
                                leftcolumn={renderContent(row.columnLeft)}
                                rightcolumn={renderContent(row.columnRight)}
                            />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};
export default StorylineBlock;
